import { useCallback, useMemo, useState } from 'react';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { DatePicker } from '@antimatterhq/ui/components/datePicker';
import { CalendarDate, type DateValue } from '@internationalized/date';
import {
  dateFromCalendarDate,
  calendarDateFromDate,
} from '@antimatterhq/ui/utils/dateUtils';
import Table, {
  TableActionsCellAction,
} from '@antimatterhq/ui/components/table';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { useTableRowSelection } from '@antimatterhq/ui/hooks/table/useTableRowSelection';
import { Badge } from '@antimatterhq/ui/components/badge';
import { formatDate } from '@antimatterhq/ui/utils/format';
import {
  dbui_v1_Notable,
  dbui_v1_NotableList,
} from '@antimatterhq/asl-api-ts-v1';
import { useVirtualServerTablePagination } from '@antimatterhq/ui/hooks/table/server/useVirtualServerTablePagination';
import { useVirtuosoTableRows } from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import { useDefaultHandleEndReached } from '@antimatterhq/ui/hooks/table/useDefaultHandleEndReached';
import { NotablesTableData } from './NotablesTableData';
import { notablesTableColumnDefinitions } from './notablesTableColumnDefinitions';
import { getBadgeVariant, getStatusText } from '@/pages/explore/notables/utils';
import SearchUserDialog from '@/components/dialog/user/SearchUserDialog';
import { AssignToButton } from '@/pages/explore/notables/_partials/AssignToButton';
import { UpdateNotablesStateSelect } from '@/pages/explore/notables/_partials/UpdateNotablesStateSelect';
import useNotables from '@/hooks/notables/useNotables';
import { useGetNotablesInfinite } from '@/hooks/dbui/useGetNotablesInfinite';
import { useNavigateWithPreviousRouteQueryParam } from '@/hooks/nagivation/useNavigateWithPreviousRouteQueryParam';

const StatusBadge = ({ state }: { state: NotablesTableData['state'] }) => {
  return (
    <Badge
      color={getBadgeVariant(state)}
      variant="filled"
      data-testid={`status-badge-${state || 'null'}`}
    >
      {getStatusText(state)}
    </Badge>
  );
};

const transformNotableData = (notable: dbui_v1_Notable): NotablesTableData => ({
  id: notable.id || '',
  eventTime: notable.collate.firstEventTime || '',
  duplicates: notable.collate.numberOfMatchingEvents,
  summary: notable.summary || '',
  state: notable.state || 'Open',
  assignedTo: notable.assignedTo || undefined,
  severity: notable.severity || 'Low',
});

const getItemsFromPage = (page: dbui_v1_NotableList) =>
  page.notables.map(transformNotableData);
// Custom hook to load all notables similar to useGetAllListRules in DetectionRulesTable

function NotablesTableContent() {
  const navigate = useNavigateWithPreviousRouteQueryParam();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigneeNotableId, setAssigneeNotableId] = useState<string | null>(
    null,
  );

  const {
    includeNotOpen,
    date,
    setIncludeNotOpen,
    setDate,
    isUpdatingNotables,
    updateNotableState,
    updateNotableAssignee,
  } = useNotables();

  const { items, isLoading, hasNextPage, fetchNextPage, error, refetch } =
    useVirtualServerTablePagination({
      queryFn: useGetNotablesInfinite,
      baseQueryParams: useMemo(
        () => ({
          includeNotOpen,
          date,
        }),
        [includeNotOpen, date],
      ),
      getItemsFromPage,
    });

  // Table hooks setup
  const {
    columns,
    columnVisibility,
    columnOrders,
    columnWidths,
    pinnedColumns,
    toggleColumnVisibility,
    resetAllColumns,
    onColumnOrderChange,
  } = useTableColumns();

  const {
    selectedRows,
    onToggleSelectAll,
    isSelected,
    isAllSelected,
    onToggleSelect,
  } = useTableRowSelection(items);

  const onSuccess = useCallback(() => {
    onToggleSelectAll(false);
  }, [onToggleSelectAll]);

  const getMenuItems = useCallback(
    (itemId: string): TableActionsCellAction[] => {
      return [
        {
          label: 'Assign to',
          icon: 'lucide:user',
          onClick: () => {
            setAssigneeNotableId(itemId);
            setShowAssignDialog(true);
          },
        },
        {
          label: 'Close as',
          icon: 'lucide:check-circle',
          subMenuItems: [
            {
              label: 'False positive',
              onClick: () =>
                updateNotableState(
                  'ClosedAsFalsePositive',
                  [itemId],
                  onSuccess,
                ),
            },
            {
              label: 'Benign positive',
              onClick: () =>
                updateNotableState(
                  'ClosedAsBenignPositive',
                  [itemId],
                  onSuccess,
                ),
            },
            {
              label: 'True positive',
              onClick: () =>
                updateNotableState(
                  'ClosedAsTruePositiveResolved',
                  [itemId],
                  onSuccess,
                ),
            },
            {
              label: 'Exported',
              onClick: () =>
                updateNotableState('ClosedAsExported', [itemId], onSuccess),
            },
          ],
        },
        {
          label: 'View',
          icon: 'lucide:eye',
          onClick: () => navigate(`/explore/notables/${itemId}`),
        },
      ];
    },
    [navigate, onSuccess, updateNotableState],
  );

  const { itemContent, fixedHeaderContent } =
    useVirtuosoTableRows<NotablesTableData>({
      columns,
      isLoading,
      customBodyCells: useMemo(
        () => ({
          state: ({ item }) => <StatusBadge state={item.state} />,
          eventTime: ({ item }) => formatDate(item.eventTime),
          duplicates: ({ item }) => item.duplicates || '-',
          edit: ({ item }) => (
            // TODO: This does rerender a bit so we should probably figure out a nicer way going forward to prevent this
            <Table.ActionsCell
              data-testid={item.id}
              actions={getMenuItems(item.id)}
            />
          ),
        }),
        [getMenuItems],
      ),
      selection: useMemo(
        () => ({
          selectedRows,
          onToggleSelectAll,
          isSelected,
          isAllSelected,
          onToggleSelect,
        }),
        [
          selectedRows,
          onToggleSelectAll,
          isSelected,
          isAllSelected,
          onToggleSelect,
        ],
      ),
    });

  // Handle filter changes
  const toggleShowAll = useCallback(
    (value: string | undefined) => {
      setIncludeNotOpen(value === 'all');
      onToggleSelectAll(false);
    },
    [setIncludeNotOpen, onToggleSelectAll],
  );

  // Convert date to CalendarDate once
  const calendarDateValue = useMemo(() => {
    return date ? calendarDateFromDate(date) : null;
  }, [date]);

  // Handle date selection
  const onDateSelected = useCallback(
    (value: DateValue | null) => {
      if (value instanceof CalendarDate) {
        setDate(dateFromCalendarDate(value));
      } else if (value === null) {
        setDate(undefined);
      }
      onToggleSelectAll(false);
    },
    [setDate, onToggleSelectAll],
  );

  const handleEndReached = useDefaultHandleEndReached(
    fetchNextPage,
    isLoading,
    hasNextPage,
  );

  return (
    <>
      <Table.VirtualizedPageContainer>
        <div className="mb-4 flex items-center justify-start gap-2">
          <div className="mr-auto flex w-full items-center gap-2">
            <DatePicker
              value={calendarDateValue}
              onChange={onDateSelected}
              placeholder="Before date"
              disabled={isLoading || isUpdatingNotables}
            />
            <ButtonGroup
              options={[
                { label: 'Open/Assigned only', value: 'openOrAssigned' },
                { label: 'Include resolved', value: 'all' },
              ]}
              onChange={toggleShowAll}
              value={includeNotOpen ? 'all' : 'openOrAssigned'}
              disabled={isLoading || isUpdatingNotables}
              id="notables-filter-group"
            />
            {selectedRows.length > 0 && (
              <>
                <AssignToButton
                  disabled={isLoading || isUpdatingNotables}
                  onClick={() => setShowAssignDialog(true)}
                />
                <UpdateNotablesStateSelect
                  disabled={isLoading || isUpdatingNotables}
                  onSelect={(value) =>
                    updateNotableState(
                      value as dbui_v1_Notable['state'],
                      selectedRows,
                      onSuccess,
                    )
                  }
                />
              </>
            )}
          </div>
          <Table.ColumnManager
            pinnedColumns={pinnedColumns}
            columnOrders={columnOrders}
            columnVisibility={columnVisibility}
            toggleColumnVisibility={toggleColumnVisibility}
            resetColumns={resetAllColumns}
            onColumnOrderChange={onColumnOrderChange}
            data-testid="notables-column-manager"
          />
        </div>

        <Table.StyleProvider
          virtualized={true}
          pageSize={items.length}
          columnWidths={columnWidths}
          columnVisibility={columnVisibility}
          data-testid="notables-table-style-provider"
        >
          <Table.Virtual
            columns={columns}
            error={error}
            refetch={refetch}
            data={items}
            itemContent={itemContent}
            fixedHeaderContent={fixedHeaderContent}
            handleEndReached={handleEndReached}
            isLoading={isLoading}
            overscan={10}
            emptyMessage="No notables found"
          />
        </Table.StyleProvider>
      </Table.VirtualizedPageContainer>

      <SearchUserDialog
        open={showAssignDialog}
        onOpenChange={setShowAssignDialog}
        onSubmit={(value) => {
          updateNotableAssignee(
            value,
            assigneeNotableId ? [assigneeNotableId] : selectedRows,
          );
          setAssigneeNotableId(null);
        }}
        title="Assign to"
      />
    </>
  );
}

export function NotablesTable() {
  return (
    <Table.Provider
      columnDefinitions={notablesTableColumnDefinitions}
      localStorageKeyPrefix="notables-table"
    >
      <NotablesTableContent />
    </Table.Provider>
  );
}
